import React from 'react';

import { getRiderLabel } from '../../../../../common/constants/rider';
import { LetterTypes } from '../generator/types';

import { BlockIcon } from './BlockIcon';
import { isSingleSelectLetterType, resolveBlockSet } from './blockResolver';
import { LetterBlock } from './blocks';
import { colors } from './drawerStyles';
import { LetterDrawerContextValue, PricingMode } from './types';

export interface SourcePanelProps {
  drawer: LetterDrawerContextValue;
}

export const SourcePanel = ({ drawer }: SourcePanelProps) => {
  const blockSet = resolveBlockSet(drawer.letterType, drawer.pricingMode);
  const sources = (Object.values(blockSet) as LetterBlock[]).filter(Boolean);
  const isOtaf = drawer.letterType === LetterTypes.otaf;
  const showRiders = isOtaf && drawer.pricingMode === 'same';

  return (
    <div
      style={{
        background: colors.surface,
        borderRight: `1px solid ${colors.border}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '10px 14px',
          borderBottom: `1px solid ${colors.border}`,
          flexShrink: 0,
        }}
      >
        <h2
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: colors.text3,
            marginBottom: 2,
          }}
        >
          Source blocks
        </h2>
        <p style={{ fontSize: 11.5, color: colors.text2 }}>
          {isSingleSelectLetterType(drawer.letterType)
            ? 'Select one'
            : 'Select one or more to combine'}
        </p>
      </div>

      {isOtaf ? <PricingToggle drawer={drawer} /> : null}
      {showRiders ? <RidersSection drawer={drawer} /> : null}
      <FcraToggle drawer={drawer} />

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          padding: 6,
        }}
      >
        {sources.map(block => (
          <SourceChip
            key={block.id}
            block={block}
            active={drawer.selectedSources.includes(block.id)}
            onClick={() => drawer.toggleSource(block.id)}
          />
        ))}
      </div>
    </div>
  );
};

const SourceChip = ({
  block,
  active,
  onClick,
}: {
  block: LetterBlock;
  active: boolean;
  onClick: () => void;
}) => (
  <div
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 7,
      padding: '6px 8px',
      borderRadius: 5,
      border: `1px solid ${active ? colors.blueBorder : 'transparent'}`,
      background: active ? colors.blueBg : 'transparent',
      cursor: 'pointer',
      userSelect: 'none',
      marginBottom: 2,
    }}
  >
    <div
      style={{
        width: 20,
        height: 20,
        borderRadius: 4,
        background: active ? colors.blueBg : colors.surface2,
        border: `1px solid ${active ? colors.blueBorder : colors.border}`,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: active ? colors.blueText : colors.text2,
      }}
    >
      <BlockIcon name={block.icon} size={12} />
    </div>
    <span
      style={{
        fontSize: 12,
        color: active ? colors.blueText : colors.text,
        flex: 1,
        lineHeight: 1.3,
        fontWeight: active ? 500 : 400,
      }}
    >
      {block.label}
    </span>
    {active ? (
      <div
        style={{
          width: 13,
          height: 13,
          borderRadius: 3,
          background: colors.blue,
          color: 'white',
          fontSize: 9,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ✓
      </div>
    ) : null}
  </div>
);

const PricingToggle = ({ drawer }: { drawer: LetterDrawerContextValue }) => {
  const isHigher = drawer.pricingMode === 'higher';
  const switchMode = (mode: PricingMode) => {
    if (mode === drawer.pricingMode) return;
    drawer.setPricingMode(mode);
  };
  return (
    <div
      style={{
        padding: '8px 14px',
        borderBottom: `1px solid ${colors.border}`,
        flexShrink: 0,
      }}
    >
      <div
        style={{
          fontSize: 9,
          fontWeight: 600,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          color: colors.text3,
          marginBottom: 6,
        }}
      >
        Pricing
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
        <PricingOption active={isHigher} onClick={() => switchMode('higher')}>
          Higher pricing
        </PricingOption>
        <PricingOption active={!isHigher} onClick={() => switchMode('same')}>
          Same pricing
        </PricingOption>
      </div>
    </div>
  );
};

const PricingOption = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <div
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 5,
      padding: '5px 8px',
      borderRadius: 5,
      border: `1px solid ${active ? colors.purpleBorder : colors.border}`,
      cursor: 'pointer',
      userSelect: 'none',
      fontSize: 11.5,
      color: active ? colors.purpleText : colors.text2,
      background: active ? colors.purpleBg : 'transparent',
      fontWeight: active ? 500 : 400,
    }}
  >
    {children}
  </div>
);

const RidersSection = ({ drawer }: { drawer: LetterDrawerContextValue }) => (
  <div
    style={{
      padding: '8px 14px',
      borderBottom: `1px solid ${colors.border}`,
      flexShrink: 0,
    }}
  >
    <div
      style={{
        fontSize: 9,
        fontWeight: 600,
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        color: colors.text3,
        marginBottom: 6,
      }}
    >
      Riders
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Mirror legacy modal (generator/index.tsx): rider options are the
          case's ineligible riders, not a hardcoded list. */}
      {drawer.riders.map((r: string) => (
        <label
          key={r}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            cursor: 'pointer',
            fontSize: 11.5,
          }}
        >
          <input
            type="checkbox"
            checked={drawer.selectedRiders.includes(r)}
            onChange={() => drawer.toggleRider(r)}
          />
          <span>{getRiderLabel(r)}</span>
        </label>
      ))}
    </div>
  </div>
);

const FcraToggle = ({ drawer }: { drawer: LetterDrawerContextValue }) => (
  <div
    onClick={() => drawer.setFcraEnabled(!drawer.fcraEnabled)}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '7px 14px',
      borderBottom: `1px solid ${colors.border}`,
      background: drawer.fcraEnabled ? colors.greenBg : colors.surface2,
      cursor: 'pointer',
      userSelect: 'none',
      flexShrink: 0,
    }}
  >
    <span
      style={{
        fontSize: 11.5,
        color: drawer.fcraEnabled ? colors.greenText : colors.text2,
        fontWeight: drawer.fcraEnabled ? 500 : 400,
      }}
    >
      📎 FCRA attachment
    </span>
    <div
      style={{
        width: 28,
        height: 16,
        borderRadius: 8,
        background: drawer.fcraEnabled ? colors.green : colors.borderStrong,
        position: 'relative',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 2,
          left: drawer.fcraEnabled ? 14 : 2,
          width: 12,
          height: 12,
          borderRadius: '50%',
          background: 'white',
          transition: 'left 0.2s',
          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }}
      />
    </div>
  </div>
);
